/**
 * Pure folder-path model for the Notebook. Operates on FOLDER paths (no
 * filename): "" is the root, nesting via "/" ("Projects/Q1"). Folders are
 * DERIVED, never stored — a note's folder IS `parentFolderOf(notePath)`
 * (path-as-identity, files-as-sole-truth). Note-path validation/branding
 * lives in `domain/notebook-path.ts` (`NotebookPath`); this module is the
 * tree math layered over those paths. No React, no Convex.
 */

/** Collapse slashes and trim so "  /Projects//Q1/ " → "Projects/Q1". */
export function normalizeFolderPath(raw: string): string {
  return raw
    .split("/")
    .map((seg) => seg.trim())
    .filter(Boolean)
    .join("/");
}

/** "Projects/Q1" → "Projects"; top-level paths parent to the root (""). */
export function parentFolderOf(path: string): string {
  const idx = path.lastIndexOf("/");
  return idx === -1 ? "" : path.slice(0, idx);
}

/** True when `candidate` is `ancestor` itself or nested anywhere under it.
 *  Segment-aware: "Projects" is NOT within "Project". */
export function isWithinFolder(candidate: string, ancestor: string): boolean {
  return candidate === ancestor || candidate.startsWith(`${ancestor}/`);
}

/** Rewrite `path` when it sits at or under `from`, grafting it onto `to`
 *  ("Projects/Q1" rebased Projects→Archive → "Archive/Q1"). Unrelated paths
 *  pass through untouched. */
export function rebaseFolderPath(path: string, from: string, to: string): string {
  return isWithinFolder(path, from) ? `${to}${path.slice(from.length)}` : path;
}

/** Merge explicit folder paths with paths implied by docs into a sorted,
 *  ancestor-complete, deduplicated list ("Projects/Q1" also yields
 *  "Projects"). Empty entries (root-dwelling docs) are dropped. */
export function collectFolderPaths(...pathLists: readonly (readonly string[])[]): string[] {
  const paths = new Set<string>();
  for (const raw of pathLists.flat()) {
    if (!raw) continue;
    const segs = raw.split("/");
    for (let i = 1; i <= segs.length; i++) paths.add(segs.slice(0, i).join("/"));
  }
  return [...paths].sort((a, b) => a.localeCompare(b));
}

export interface FolderNode {
  name: string;
  path: string;
  children: FolderNode[];
}

/** Build the sorted folder tree from any mix of explicit + doc-implied
 *  paths. Ancestors materialize automatically via `collectFolderPaths`. */
export function buildFolderTree(...pathLists: readonly (readonly string[])[]): FolderNode[] {
  const roots: FolderNode[] = [];
  const byPath = new Map<string, FolderNode>();
  for (const path of collectFolderPaths(...pathLists)) {
    const node: FolderNode = { name: path.split("/").pop() ?? path, path, children: [] };
    byPath.set(path, node);
    const parent = byPath.get(parentFolderOf(path));
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}
